import React from 'react';
import { Selection } from '../types';
import { generateTimeSlots, isSameTimeSlot } from '../utils/timeUtils';
import './TimeGrid.css';

interface TimeGridProps {
  selections: Selection[];
  onSlotClick: (timeSlot: string) => void;
  currentUser: string | null;
}

const TimeGrid: React.FC<TimeGridProps> = ({ selections, onSlotClick, currentUser }) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const hasScrolled = React.useRef(false);

  // Generate 48 time slots (00:00 - 23:30)
  const timeSlots = generateTimeSlots();

  // Auto-scroll to 10am on mount
  React.useEffect(() => {
    if (hasScrolled.current || !wrapperRef.current) return;

    const targetIndex = 20; // 10:00 AM (index 20 = 10*2)
    const slotWidth = 62; // 60px + 2px gap
    const scrollPosition = targetIndex * slotWidth;

    wrapperRef.current.scrollLeft = scrollPosition;
    hasScrolled.current = true;
  }, []);

  // Helper function to check if a slot is selected by a participant
  const isSlotSelected = (selection: Selection, slotTime: Date): boolean => {
    return selection.timeSlots.some(timeSlot =>
      isSameTimeSlot(timeSlot, slotTime)
    );
  };

  return (
    <div className="time-grid-container">
      <div className="time-grid-layout">
        {/* Names column - fixed */}
        <div className="names-column">
          <div className="participant-name-cell header-cell"></div>
          {selections.map((selection) => (
            <div key={selection.userName} className="participant-name-cell">
              {selection.userName}
            </div>
          ))}
          {!selections.find(s => s.userName === currentUser) && (
            <div className="participant-name-cell current-user-name">
              {currentUser || 'Вы'}
            </div>
          )}
        </div>

        {/* Scrollable time slots area */}
        <div className="time-slots-wrapper" ref={wrapperRef}>
          {/* Header row with time labels */}
          <div className="time-labels-row">
            {timeSlots.map((slot, index) => (
              <div key={index} className="time-label">
                {slot.label}
              </div>
            ))}
          </div>

          {/* Rows for each participant */}
          {selections.map((selection) => {
            const isCurrentUserRow = selection.userName === currentUser;
            return (
              <div key={selection.userName} className="time-slots-row">
                {timeSlots.map((slot, index) => {
                  const isSelected = isSlotSelected(selection, slot.time);
                  return (
                    <div
                      key={index}
                      className={`time-slot ${isSelected ? 'selected' : ''} ${isCurrentUserRow ? 'clickable' : ''}`}
                      style={{
                        backgroundColor: isSelected ? selection.color : 'transparent'
                      }}
                      onClick={() => isCurrentUserRow && onSlotClick(slot.time.toISOString())}
                    />
                  );
                })}
              </div>
            );
          })}

          {/* Empty row for current user to add selections */}
          {!selections.find(s => s.userName === currentUser) && (
            <div className="time-slots-row current-user-row">
              {timeSlots.map((slot, index) => (
                <div
                  key={index}
                  className="time-slot clickable"
                  onClick={() => onSlotClick(slot.time.toISOString())}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeGrid;
